import React, { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 20px;
  border: 2px solid #000;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
  margin: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NavLink = styled(Link)`
  color: #000;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  
  &:hover {
    background-color: #f8f9fa;
    border-color: #000;
    transform: translateY(-1px);
  }
  
  &.active {
    background-color: #000;
    color: white;
    border-color: #000;
  }
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
`;

const Main = styled.main`
  max-width: 800px;
  margin: 0 auto;
`;

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <Container>
      <Header>
        <Logo src="/logo.png" alt="KEEPS Logo" />
        {user && (
          <Navigation>
            {user.is_admin ? (
              <>
                <NavLink 
                  to="/admin" 
                  className={location.pathname === '/admin' ? 'active' : ''}
                >
                  Admin Dashboard
                </NavLink>
                <NavLink 
                  to="/admin/registrations" 
                  className={location.pathname === '/admin/registrations' ? 'active' : ''}
                >
                  Registrations
                </NavLink>
                <NavLink 
                  to="/" 
                  className={location.pathname === '/' ? 'active' : ''}
                >
                  User View
                </NavLink>
              </>
            ) : (
              <>
                <NavLink 
                  to="/" 
                  className={location.pathname === '/' ? 'active' : ''}
                >
                  New Registration
                </NavLink>
                <NavLink 
                  to="/my-requests" 
                  className={location.pathname === '/my-requests' ? 'active' : ''}
                >
                  My Requests
                </NavLink>
              </>
            )}
            <UserInfo>
              <span style={{ color: '#000', fontWeight: '500' }}>Welcome, {user.email}</span>
              {user.is_admin && <span style={{ color: '#28a745', fontWeight: 'bold', background: '#e8f5e8', padding: '0.25rem 0.5rem', borderRadius: '15px', fontSize: '0.8rem' }}>(Admin)</span>}
              <LogoutButton onClick={logout}>Logout</LogoutButton>
            </UserInfo>
          </Navigation>
        )}
      </Header>
      <Main>{children}</Main>
    </Container>
  );
};

export default Layout;